import React, { useEffect, useState } from 'react';
import { useAppContext } from '../../share-components/AppContext';
import { useNavigate, useParams } from 'react-router-dom';
import { ROUTE_ADD_MONEY_ADJUSTMENT,  ROUTE_EDIT_MONEY_ADJUSTMENT, ROUTE_MONEY_ADJUSTMENT_LIST } from '../../constants/routeConstants';
import { Button, Card } from 'react-bootstrap';
import { customStorage, formatDateTime, getPayloadData, isInRole, isNullOrEmptyString, isNullOrUndefinedObj } from '../../services/UtilityService';
import AlertDialog from '../../share-components/AlertDialog';
import ConfirmDialog from '../../share-components/ConfirmDialog';
import { getLastLockedYearMonth } from '../../services/CommonService';
import FormTitle from '../../share-components/FormTitle';

import { SERVER_ERROR_MESSAGE, TOKEN_SESSION_KEY } from '../../constants/commonConstants';
import { cancelMoneyAdjustment, getMoneyAdjustmentDetails } from '../../services/MoneyAdjustmentService';
import { RG_ADMIN_ONLY } from '../../constants/roleConstants';

const MoneyAdjustmentDetails = () => {
    const token = customStorage.getItem(TOKEN_SESSION_KEY);
    const currentUser = getPayloadData(token);

    const { moneyAdjustmentId } = useParams();
    const { setLoading, setShowApiErrorMessage, setRouteNaviData } = useAppContext();
    const [showSuccessDialog, setShowSuccessDialog] = useState(false);
    const [showConfirmDialog, setShowConfirmDialog] = useState(false);
    const [isFirstLoaded, setIsFirstLoaded] = useState(false);
    const [lastLockedYearMonth, setLastLockedYearMonth] = useState(null);
    const [moneyAdjustmentData, setMoneyAdjustmentData] = useState(null);
    const navigate = useNavigate();

    const initialLoad = async () => {
        setLoading(true);
        try {
            const lastLockedData = await getLastLockedYearMonth();
            setLastLockedYearMonth(lastLockedData || null);

            const response = await getMoneyAdjustmentDetails((parseInt(moneyAdjustmentId) || 0));
            setMoneyAdjustmentData(response?.moneyAdjustmentDetailsInfo);
            setIsFirstLoaded(true);
        } catch (error) {
            const errMsg = error?.response?.data?.errorMessage || SERVER_ERROR_MESSAGE;
            if (error.status !== 401 && error.status !== 403) {
                setShowApiErrorMessage(errMsg);
            }
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        setRouteNaviData("MONEY ADJUSTMENT >> ADJUSTMENT DETAILS");
        initialLoad();
    }, []);

    const handleCancelMoneyAdjustment = async () => {
        setShowConfirmDialog(false);
        setLoading(true);
        try {
            await cancelMoneyAdjustment(moneyAdjustmentId);
            setShowSuccessDialog(true);
        } catch (error) {
            const errMsg = error?.response?.data?.errorMessage || SERVER_ERROR_MESSAGE;
            if (error.status !== 401 && error.status !== 403) {
                setShowApiErrorMessage(errMsg);
            }
        } finally {
            setLoading(false);
        }
    };

    const handleSuccessDialogClose = () => {
        setShowSuccessDialog(false);
        navigate(ROUTE_MONEY_ADJUSTMENT_LIST);
    };

    const canValidProcess = () => {
        const dateStr = moneyAdjustmentData?.paymentDate || '';
        const paymentYear = dateStr.length >= 4 ? parseInt(dateStr.slice(0, 4), 10) : 0;
        const paymentMonth = dateStr.length >= 6 ? parseInt(dateStr.slice(4, 6), 10) : 0;
        const isValidUser = !isNullOrEmptyString(moneyAdjustmentData?.paidById) && moneyAdjustmentData?.paidById === currentUser?.userId;
        const isValidLockDate = isNullOrUndefinedObj(lastLockedYearMonth) || lastLockedYearMonth.toYear < paymentYear ||
            (lastLockedYearMonth.toYear === paymentYear && lastLockedYearMonth.toMonth < paymentMonth);
        return isValidUser && isValidLockDate;
    }

    return (
        <div id='moneyAdjustmentDetailsPage' style={{ paddingBottom: '50px' }}>
            <FormTitle title={"Adjustment Details"} isCenter={true} />

            {isFirstLoaded && <>
                <Card style={{ padding: '10px', margin: '0 auto', maxWidth: '650px' }}>
                    <Card.Body className='pt-2'>
                        <div className="container">
                            <div className="row justify-content-center">
                                <div style={{ maxWidth: "550px" }}>
                                    <div className="mb-1">
                                        <label className='custom-label'>Payment Amount</label>
                                        <div className="w-100 label-box">
                                            {moneyAdjustmentData?.paymentAmount}¥
                                        </div>
                                    </div>

                                    <div className="mb-1">
                                        <label className='custom-label'>Payment Date(yyyyMMdd)</label><br />
                                        <div className="w-100 label-box">
                                            {formatDateTime(moneyAdjustmentData?.paymentDate)}
                                        </div>
                                    </div>

                                    <div className="mb-1">
                                        <label className='custom-label'>Paid By {'=>'} Accepted By</label>
                                        <div className="w-100 label-box">
                                            {moneyAdjustmentData?.paidBy} {'=>'} {moneyAdjustmentData?.acceptedBy}
                                        </div>
                                    </div>

                                    {/* Comment */}
                                    <div className="mb-1">
                                        <label className='custom-label'>Comment</label>
                                        <div className="w-100 label-box input-area input-area">
                                            {moneyAdjustmentData?.comment}
                                        </div>
                                    </div>

                                    <div className='floating-bottom-btn-group mt-2'>
                                        <div className='floating-bottom-btn-grid'>
                                            <Button
                                                className="appBtn w-100"
                                                type="button"
                                                variant="secondary"
                                                onClick={() => navigate(ROUTE_MONEY_ADJUSTMENT_LIST)}
                                            >
                                                Go to Adjustment List
                                            </Button>
                                            {isInRole(RG_ADMIN_ONLY, currentUser?.role) && <Button
                                                className="appBtn w-100"
                                                type="button"
                                                variant="warning"
                                                onClick={() => navigate(`${ROUTE_ADD_MONEY_ADJUSTMENT}`)}
                                            >
                                                Add New Adjustment
                                            </Button>}
                                            {canValidProcess() && <Button
                                                className="appBtn w-100"
                                                type="button"
                                                variant="secondary"
                                                onClick={() => navigate(`${ROUTE_EDIT_MONEY_ADJUSTMENT}/${moneyAdjustmentId}`)}
                                            >
                                                Edit
                                            </Button>}
                                            {canValidProcess() &&
                                                <Button
                                                    className="appBtn w-100"
                                                    type="button"
                                                    variant="danger"
                                                    onClick={() => setShowConfirmDialog(true)}
                                                >
                                                    Cancel Adjustment
                                                </Button>}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </Card.Body>
                </Card>
                <ConfirmDialog
                    dialogId='cancelMoneyAdjustmentConfirmDialog'
                    showDialog={showConfirmDialog}
                    body='Are you sure to cancel this money adjustment?'
                    onConfirm={handleCancelMoneyAdjustment}
                    onCancel={() => { setShowConfirmDialog(false); }}
                />
                <AlertDialog
                    dialogId={'successDialog'}
                    showDialog={showSuccessDialog}
                    body={'Your money adjustment has been successfully cancelled.'}
                    onConfirm={handleSuccessDialogClose}
                />
            </>}
        </div>
    );
};

export default MoneyAdjustmentDetails;
